import { addDays, dayOfMonth, isPast, monthName, today, weekdayName, workWeek } from '../dates';
import type { MyBooking } from '../types';

interface Props {
  date: string;
  onChange: (date: string) => void;
  bookings: MyBooking[];
  /** Last bookable date. Days past it are shown but not selectable. */
  bookableUntil?: string | null;
}

export function WeekSpine({ date, onChange, bookings, bookableUntil }: Props) {
  const days = workWeek(date);
  const first = days[0] ?? date;
  const last = days[4] ?? date;

  const held = new Map<string, Set<string>>();
  for (const booking of bookings) {
    const kinds = held.get(booking.date) ?? new Set<string>();
    kinds.add(booking.kind);
    held.set(booking.date, kinds);
  }

  return (
    <div className="spine">
      <div className="spine-head">
        <h2>
          {monthName(first)} {dayOfMonth(first)} – {dayOfMonth(last)}
        </h2>
        <div className="row" style={{ gap: 4 }}>
          <button className="quiet" onClick={() => onChange(addDays(first, -7))} aria-label="Previous week">
            ‹
          </button>
          <button className="quiet" onClick={() => onChange(today())}>
            Today
          </button>
          <button className="quiet" onClick={() => onChange(addDays(first, 7))} aria-label="Next week">
            ›
          </button>
        </div>
      </div>

      <div className="spine-days">
        {days.map((day) => {
          const kinds = held.get(day);
          const tooFar = Boolean(bookableUntil) && day > bookableUntil!;
          const closed = isPast(day) || tooFar;
          return (
            <button
              key={day}
              className={`day${day === today() ? ' is-today' : ''}`}
              aria-pressed={day === date}
              disabled={closed}
              title={tooFar ? 'Bookings for this day have not opened yet' : undefined}
              onClick={() => onChange(day)}
            >
              <span className="num">{dayOfMonth(day)}</span>
              <span className="name">{weekdayName(day)}</span>
              <span className="marks">
                <span
                  className={`mark desk${kinds?.has('desk') ? ' on' : ''}`}
                  title={kinds?.has('desk') ? 'Desk booked' : undefined}
                />
                <span
                  className={`mark parking${kinds?.has('parking') ? ' on' : ''}`}
                  title={kinds?.has('parking') ? 'Parking booked' : undefined}
                />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
